import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { fromJS } from 'immutable';

import LoadAnimation from '../../decorators/LoadAnimation';
import BaseInput from '../../helpers/Input';
import BaseSelect from '../../helpers/Select';
import { InputProps } from '../../helpers/AutoComplete';
import FormGroup from '../../helpers/FormGroup';
import FormSubmitOnBlur from '../../helpers/FormSubmitOnBlur';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';

import ContactExecutiveDealNotes from './ContactExecutiveDealNotes';
import ContactExecutiveInfo from './ContactExecutiveInfo';
import ContactExecutiveDetailsPanel from './ContactExecutiveDetailsPanel';
import ContactExecutiveEmailPanel from './ContactExecutiveEmailPanel';
import ContactExecutiveFunds from './ContactExecutiveFunds';
import ContactExecutivePositions from './ContactExecutivePositions';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));
const Select = PrettyError(DisplayError(BaseSelect));

const ContactExecutive = props => {
  const setPositionPanel = useCallback(() => {
    const rightPanel = document.querySelector('#position_right_panel');
    const execRowPanel = document.querySelector('#exec_row_panel');

    if (rightPanel && execRowPanel) {
      rightPanel.style.height = `${execRowPanel.offsetHeight}px`;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', setPositionPanel);
    setPositionPanel();

    return () => {
      window.removeEventListener('resize', setPositionPanel);
    };
  }, [setPositionPanel]);

  return ContactExecutiveRender(props);
};

const ContactExecutiveRender = props => {
  const {
    highestFundIndex,
    suggestDirector,
    suggestAnalyst,
    suggestDealmaker,
    suggestResearcher,
    dealNotes,
    emails,
    contact,
    statuses,
    qualities,
    interests,
    relations,
    qualitiesOfExecutive,
    suggestIndustry,
    deleteIndustry,
    onSubmit,
    onBlur,
    onBlurDetails,
    onSubmitDetails,
    onClickIndustries,
    addIndustry,
    onClickTags,
    addTag,
    deleteTag,
    onEditEvent,
    onEmailMouseEnter,
    onDialNotesMouseEnter,
    onEmailMouseLeave,
    onDialNotesMouseLeave,
    addFund,
    triggerFund,
    deleteFund,
    onSubmitFund,
    onBlurFund,
    activateFund,
    suggestProject,
    suggestTitles,
    addPosition,
    triggerPosition,
    onSubmitPosition,
    onBlurPosition,
    deletePosition,
    onChange,
  } = props;

  const loadingFunds = contact.get('loadingFunds');
  const funds = contact.get('funds');
  const lastResearchedDate = contact.get('lastResearchedDate');
  const loadingPositions = contact.get('loadingPositions');
  const positions = contact.get('positions');

  const suggestions = contact.get('suggests');
  const employmentStatus = contact.get('isEmployed')
    ? 'Employed'
    : contact.get('isEmployed') === false
    ? 'Transition'
    : null;
  const fundPrefix = `funds.${highestFundIndex}`;

  return (
    <div className={classNames(style.host, 'tab-pane active full-height')} id="slide-3">
      <div className={classNames(style.status, 'row')}>
        <FormSubmitOnBlur
          className={classNames(style.statusBar, 'form-inline')}
          onChange={onChange}
          onComponentBlur={onBlur}
          onSubmit={onSubmit}
        >
          <div className="row d-flex">
            <div className="col-xs-12 col-sm-3 col-xl-2">
              <label className="field-label">Module</label>
              <InputProps className="form-control input-sm" name="suggestDirector" placeholder="Module">
                {suggestDirector}
              </InputProps>
            </div>

            <div className="col-xs-12 col-sm-3 col-xl-2">
              <label className="field-label">Dealmaker 1</label>
              <InputProps className="form-control input-sm" name="suggestAnalyst" placeholder="Dealmaker 1">
                {suggestAnalyst}
              </InputProps>
            </div>

            <div className="col-xs-12 col-sm-3 col-xl-2">
              <label className="field-label">Dealmaker 2</label>
              <InputProps className="form-control input-sm" name="suggestDealmaker" placeholder="Dealmaker 2">
                {suggestDealmaker}
              </InputProps>
            </div>

            <div className="col-xs-12 col-sm-5 col-xl-2">
              <FormGroup>
                <Select
                  emptyValue=""
                  id="f-status"
                  label="Status"
                  name="currentStatus"
                  options={fromJS(statuses)}
                  prefix="funds.0"
                  root={contact}
                  selectClassName="input-sm"
                  deep
                  showLabel
                  showValue
                />
              </FormGroup>
            </div>

            <div className="col-xs-12 col-sm-3 col-xl-2">
              <FormGroup>
                <Input
                  className="input-sm"
                  emptyValue=""
                  id="f-High"
                  label="High"
                  name="highStatus"
                  placeholder="High"
                  prefix={fundPrefix}
                  root={contact}
                  deep
                  readOnly
                  showLabel
                />
              </FormGroup>
            </div>

            <div className={classNames(style.employment, 'col-xs-12 col-sm-2 col-xl-1')}>
              {employmentStatus && (
                <span
                  className={classNames(style.employmentChip, {
                    [style.employmentChipTransition]: contact.get('isEmployed') === false,
                  })}
                >
                  {employmentStatus}
                </span>
              )}
            </div>

            <div className="col-xs-4 text-right">
              <FormGroup>
                <label className="field-label">Researcher</label>
                <InputProps
                  className="form-control input-sm"
                  name="suggestResearcher"
                  placeholder="Researcher"
                >
                  {suggestResearcher}
                </InputProps>
              </FormGroup>
            </div>
            <div className="col-xs-12 col-sm-3 col-xl-1">
              <FormGroup>
                <Input
                  className="input-sm"
                  id="f-researched-date"
                  label="Last Researched"
                  name="lastResearchedDate"
                  onChange={onChange}
                  placeholder="Last Researched"
                  type="date"
                  value={lastResearchedDate}
                  showLabel
                />
              </FormGroup>
            </div>
          </div>
        </FormSubmitOnBlur>
      </div>

      <div className={classNames(style.body, 'flex-grid flex-wrap-md row p-relative')} id="exec_row_panel">
        <ContactExecutiveDealNotes
          dealNotes={dealNotes}
          onEditEvent={onEditEvent}
          onMouseEnter={onDialNotesMouseEnter}
          onMouseLeave={onDialNotesMouseLeave}
        />
        <section className="flex-grow1 container-fluid">
          <div className="row">
            <div className="col-xs-6 col-xl-7 col-xxl-8">
              <FormSubmitOnBlur
                className={classNames(style.accordion, 'panel-group accordion-middle-side')}
                id="accordion"
                onComponentBlur={onBlurDetails}
                onSubmit={onSubmitDetails}
              >
                <ContactExecutiveInfo
                  contact={contact}
                  interests={interests}
                  onChange={onChange}
                  qualities={qualities}
                  qualitiesOfExecutive={qualitiesOfExecutive}
                  relations={relations}
                />

                <ContactExecutiveDetailsPanel
                  addIndustry={addIndustry}
                  addTag={addTag}
                  contact={contact}
                  deleteIndustry={deleteIndustry}
                  deleteTag={deleteTag}
                  onChange={onChange}
                  onClickIndustries={onClickIndustries}
                  onClickTags={onClickTags}
                  suggestIndustry={suggestIndustry}
                  suggestions={suggestions}
                />

                <ContactExecutiveEmailPanel
                  emails={emails}
                  onEditEvent={onEditEvent}
                  onMouseEnter={onEmailMouseEnter}
                  onMouseLeave={onEmailMouseLeave}
                />
              </FormSubmitOnBlur>
            </div>
            <div
              className={classNames(style.rightPanel, 'col-xs-6 col-xl-5 col-xxl-4 panel-auto-scroll-y')}
              id="position_right_panel"
            >
              <div className={classNames(style.sidebar, 'container-fluid')}>
                <div className="row">
                  <ContactExecutivePositions
                    addPosition={addPosition}
                    loading={loadingPositions}
                    onBlur={onBlurPosition}
                    onChange={onChange}
                    onClick={triggerPosition}
                    onDelete={deletePosition}
                    onSubmit={onSubmitPosition}
                    positions={positions}
                    suggestions={suggestions}
                    suggestTitles={suggestTitles}
                  />
                </div>
                <div className="row">
                  <ContactExecutiveFunds
                    addFund={addFund}
                    funds={funds}
                    loading={loadingFunds}
                    onActivate={activateFund}
                    onBlur={onBlurFund}
                    onClick={triggerFund}
                    onDelete={deleteFund}
                    onSubmit={onSubmitFund}
                    suggestProject={suggestProject}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

ContactExecutiveRender.propTypes = {
  onChange: PropTypes.func.isRequired,
};

export default LoadAnimation(ContactExecutive);
